import React, { useEffect, useState } from 'react';
import { Bell, GraduationCap, Search, ShieldCheck, LayoutDashboard, FileText, Compass, BookOpen, Cloud, CloudOff, RefreshCw, LogOut, User, Menu } from 'lucide-react';
import { AuditInstance, LearningProgress } from '../../types';
import { cloudSyncService, CloudSyncStatus } from '../../services/cloudSyncService';

interface AppHeaderProps {
  audit: AuditInstance | null;
  activeView: string;
  progress?: LearningProgress;
  onNavigateView: (view: 'learning' | 'keyword_research' | 'content_planning' | 'dashboard' | 'workspace' | 'report' | 'onpage') => void;
  onLogout?: () => void;
  onToggleMobileMenu?: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  activeView,
  onNavigateView,
  onLogout,
  onToggleMobileMenu,
}) => {
  const [cloudStatus, setCloudStatus] = useState<CloudSyncStatus>('local_only');
  const [isManualSyncing, setIsManualSyncing] = useState(false);

  useEffect(() => {
    const unsubscribe = cloudSyncService.subscribe((status) => {
      setCloudStatus(status);
    });
    return unsubscribe;
  }, []);

  const handleManualSync = async () => {
    setIsManualSyncing(true);
    await cloudSyncService.checkCloudConnection();
    setTimeout(() => {
      setIsManualSyncing(false);
    }, 600);
  };

  return (
    <header
      style={{
        height: '64px',
        background: '#070c16',
        borderBottom: '1px solid #1e293b',
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 80,
        userSelect: 'none',
        gap: '12px',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      {/* Left Area: Mobile Menu Toggle & Navigation Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
        {onToggleMobileMenu && (
          <button
            onClick={onToggleMobileMenu}
            className="mobile-only"
            title="Toggle Navigation Menu"
            style={{
              padding: '8px',
              borderRadius: '8px',
              background: 'rgba(15, 23, 42, 0.8)',
              border: '1px solid #1e293b',
              color: '#94a3b8',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Menu size={18} />
          </button>
        )}

        {/* Top Navigation Tabs (Scrollable on small screens) */}
        <div
          className="header-tabs-scroll"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            overflowX: 'auto',
            flexShrink: 1,
            paddingRight: '6px',
          }}
        >
        {/* 1. Learning Tab */}
        <button
          onClick={() => onNavigateView('learning')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: (activeView === 'learning' || activeView === 'lesson') ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
            color: (activeView === 'learning' || activeView === 'lesson') ? '#ffffff' : '#94a3b8',
            transition: 'all 0.15s ease',
            boxShadow: (activeView === 'learning' || activeView === 'lesson') ? '0 2px 10px rgba(2, 132, 199, 0.3)' : 'none',
          }}
        >
          <GraduationCap size={16} color={(activeView === 'learning' || activeView === 'lesson') ? '#ffffff' : '#64748b'} />
          <span>Learning</span>
        </button>

        {/* 2. Audit Tab */}
        <button
          onClick={() => onNavigateView('dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: (activeView === 'dashboard' || activeView === 'workspace' || activeView === 'report') ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
            color: (activeView === 'dashboard' || activeView === 'workspace' || activeView === 'report') ? '#ffffff' : '#94a3b8',
            transition: 'all 0.15s ease',
            boxShadow: (activeView === 'dashboard' || activeView === 'workspace' || activeView === 'report') ? '0 2px 10px rgba(2, 132, 199, 0.3)' : 'none',
          }}
        >
          <ShieldCheck size={16} color={(activeView === 'dashboard' || activeView === 'workspace' || activeView === 'report') ? '#ffffff' : '#64748b'} />
          <span>Audit</span>
        </button>

        {/* 3. Research Tab */}
        <button
          onClick={() => onNavigateView('keyword_research')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: (activeView === 'keyword_research' || activeView === 'keyword_workspace') ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
            color: (activeView === 'keyword_research' || activeView === 'keyword_workspace') ? '#ffffff' : '#94a3b8',
            transition: 'all 0.15s ease',
            boxShadow: (activeView === 'keyword_research' || activeView === 'keyword_workspace') ? '0 2px 10px rgba(2, 132, 199, 0.3)' : 'none',
          }}
        >
          <Search size={16} color={(activeView === 'keyword_research' || activeView === 'keyword_workspace') ? '#ffffff' : '#64748b'} />
          <span>Research</span>
        </button>

        {/* 4. Content Planning Tab */}
        <button
          onClick={() => onNavigateView('content_planning')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: (activeView === 'content_planning' || activeView === 'content_planning_workspace') ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
            color: (activeView === 'content_planning' || activeView === 'content_planning_workspace') ? '#ffffff' : '#94a3b8',
            transition: 'all 0.15s ease',
            boxShadow: (activeView === 'content_planning' || activeView === 'content_planning_workspace') ? '0 2px 10px rgba(2, 132, 199, 0.3)' : 'none',
          }}
        >
          <BookOpen size={16} color={(activeView === 'content_planning' || activeView === 'content_planning_workspace') ? '#ffffff' : '#64748b'} />
          <span>Content Planning</span>
        </button>

        {/* 5. On Page Tab */}
        <button
          onClick={() => onNavigateView('onpage')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '10px',
            border: 'none',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: (activeView === 'onpage' || activeView === 'onpage_workspace') ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'transparent',
            color: (activeView === 'onpage' || activeView === 'onpage_workspace') ? '#ffffff' : '#94a3b8',
            transition: 'all 0.15s ease',
            boxShadow: (activeView === 'onpage' || activeView === 'onpage_workspace') ? '0 2px 10px rgba(2, 132, 199, 0.3)' : 'none',
          }}
        >
          <Compass size={16} color={(activeView === 'onpage' || activeView === 'onpage_workspace') ? '#ffffff' : '#64748b'} />
          <span>On Page</span>
        </button>
        </div>
      </div>

      {/* Top Right Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {/* Cloudflare D1 Sync Status Pill */}
        <button
          onClick={handleManualSync}
          title="Cloudflare D1 Database & Storage Status. Click to test connection/sync."
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '8px',
            background: cloudStatus === 'connected' || cloudStatus === 'synced'
              ? 'rgba(16, 185, 129, 0.1)'
              : 'rgba(245, 158, 11, 0.1)',
            border: `1px solid ${cloudStatus === 'connected' || cloudStatus === 'synced' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
            color: cloudStatus === 'connected' || cloudStatus === 'synced' ? '#34d399' : '#fbbf24',
            fontSize: '11.5px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
          }}
        >
          {cloudStatus === 'connected' || cloudStatus === 'synced' ? (
            <Cloud size={13} color="#34d399" />
          ) : cloudStatus === 'syncing' || isManualSyncing ? (
            <RefreshCw size={13} color="#38bdf8" style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <CloudOff size={13} color="#fbbf24" />
          )}
          <span className="desktop-only">
            {cloudStatus === 'connected' || cloudStatus === 'synced'
              ? 'Cloud D1 Synced'
              : cloudStatus === 'syncing' || isManualSyncing
              ? 'Syncing...'
              : 'D1 Ready (Local)'}
          </span>
        </button>

        {/* Audit Dashboard Action Button */}
        <button
          className="desktop-only"
          onClick={() => onNavigateView('dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            padding: '7px 14px',
            borderRadius: '9px',
            background: activeView === 'dashboard' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'rgba(15, 23, 42, 0.8)',
            border: '1px solid #1e293b',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s ease',
          }}
        >
          <LayoutDashboard size={14} color={activeView === 'dashboard' ? '#ffffff' : '#94a3b8'} />
          <span>Audit Dashboard</span>
        </button>

        {/* Final Reports Action Button */}
        <button
          className="desktop-only"
          onClick={() => onNavigateView('report')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            padding: '7px 14px',
            borderRadius: '9px',
            background: activeView === 'report' ? 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)' : 'rgba(15, 23, 42, 0.8)',
            border: '1px solid #1e293b',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.15s ease',
          }}
        >
          <FileText size={14} color={activeView === 'report' ? '#ffffff' : '#94a3b8'} />
          <span>Final Reports</span>
        </button>

        {/* Notifications Icon */}
        <div
          className="desktop-only"
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '50%',
            background: 'rgba(15, 23, 42, 0.8)',
            border: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            cursor: 'pointer',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <Bell size={15} />
          <span
            style={{
              position: 'absolute',
              top: '7px',
              right: '7px',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#f43f5e',
            }}
          />
        </div>

        {/* User Session & Logout */}
        {onLogout && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderLeft: '1px solid #1e293b', paddingLeft: '8px' }}>
            <div
              className="desktop-only"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '5px 9px',
                borderRadius: '8px',
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <User size={13} color="#38bdf8" />
              <span>ihamza</span>
            </div>

            <button
              onClick={onLogout}
              title="Sign Out"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '6px 10px',
                borderRadius: '8px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#f87171',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              }}
            >
              <LogOut size={13} />
              <span className="desktop-only">Sign Out</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
};
